import React, { useState } from 'react';
import moving_logo from './sheep.png';
import title_logo from './npc_logo.png';
import './App.css';
import './Buttons.css';
import './FilterStyles.css';
import './LayoutFixes.css';
import './LayoutOverrides.css';
import './NewLayout.css';
import './CommandEditorFixes.css';
import './components/FormLayoutFixes.css';

// Import components
import Header from './components/Header/Header';
import CommandEditor from './components/CommandEditor/CommandEditor';
import FileImporter from './components/FileHandlers/FileImporter';
import DialogueEditor from './components/NPCEditor/DialogueEditor';
import ButtonEditor from './components/NPCEditor/ButtonEditor';

// Import context provider and hook
import { NPCProvider, useNPC } from './context/NPCContext';

// Import new components

import EnhancedCommandEditor from './components/CommandEditor/enhancedCommandEditor';
import EnhancedFileExporter from './components/FileHandlers/enhancedFileExporter';
import ConfigurationDialog, { EnhancedNPCEditor } from './components/configurationDialog'
/**
 * Main App component with configuration system
 */
function App() {
  const [showConfig, setShowConfig] = useState(true);
  const [config, setConfig] = useState(null);
  const [screen, setScreen] = useState(1);
  
  const handleConfigComplete = (userConfig) => {
    // Process configuration defaults
    const processedConfig = {
      ...userConfig,
      // Set defaults for "not sure" answers
      npcType: userConfig.npcType === 'not_sure' ? 'default' : userConfig.npcType,
      sceneTracking: userConfig.sceneTracking === 'not_sure' ? 'scores' : userConfig.sceneTracking
    };
    
    setConfig(processedConfig);
    setShowConfig(false);
    
    //console.log('Configuration set:', processedConfig);
  };
  
  if (showConfig) {
    return <ConfigurationDialog onConfigComplete={handleConfigComplete} />;
  }
  
  return (
    <NPCProvider>
      <AppContent screen={screen} setScreen={setScreen} config={config} />
    </NPCProvider>
  );
}

/**
 * Content component that uses the NPC context with configuration
 */
function AppContent({ screen, setScreen, config }) {
  // Get all NPC data from context
  const npcData = useNPC();
  
  // Additional state for custom entities
  const [currentEntityId, setCurrentEntityId] = useState('');
  const [entityIds, setEntityIds] = useState({}); // Maps NPC names to entity IDs
  
  return (
    <div className="App">
      {screen === 1 ? (
        // Main NPC Editor Screen
        <Header
          logoSrc={moving_logo}
          titleLogoSrc={title_logo}
          headerClass="App-header"
        >
          <div className="main-layout-container">
            {/* Configuration indicator */}
            <div style={{
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              padding: '8px 16px',
              borderRadius: '20px',
              marginBottom: '20px',
              fontSize: '0.8rem',
              color: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <strong>Configuration:</strong> {config.npcType === 'custom' ? 'Custom Entities' : 'Default NPCs'} • {config.sceneTracking === 'scripts' ? 'Script-based' : 'Score-based'} tracking
              {config.tutorial === 'tutorial' && ' • Tutorial mode enabled'}
            </div>
            
            {/* Row 1: Input form and Import/Export */}
            <div className="main-layout-row no-logo">
              {/* Column 1: Input form */}
              <div className="form-section expanded">
                <EnhancedNPCEditor 
                  {...npcData}
                  config={config}
                  currentEntityId={currentEntityId}
                  setCurrentEntityId={setCurrentEntityId}
                  entityIds={entityIds}
                  setEntityIds={setEntityIds}
                />
              </div>
              
              {/* Column 2: Import/Export buttons */}
              <div className="buttons-section">
                <FileImporter />
                <EnhancedFileExporter 
                  npcNames={npcData.npcNames}
                  dialogues={npcData.dialogues}
                  buttons={npcData.buttons}
                  commands={npcData.commands}
                  entityIds={entityIds}
                  config={config}
                />
              </div>
            </div>
            
            {/* Row 2: Dialogue & Button Editors */}
            <div className="editors-row">
              <div className="editors-container">
                <div className="dialogue-editor-column">
                  <DialogueEditor 
                    selectedNpc={npcData.selectedNpc}
                    dialogues={npcData.dialogues}
                    setEditingDialogue={npcData.setEditingDialogue}
                    setCurrentDialogue={npcData.setCurrentDialogue}
                  />
                </div>
                
                <div className="button-editor-column">
                  <ButtonEditor 
                    selectedNpc={npcData.selectedNpc}
                    dialogues={npcData.dialogues}
                    buttons={npcData.buttons}
                    setButtons={npcData.setButtons}
                  />
                </div>
              </div>
            </div>
            
            {/* Row 3: Edit Commands button */}
            <div className="commands-button-row">
              <button 
                className="button-19 screen-change-button"
                onClick={() => setScreen(2)}
              >
                Edit Commands
              </button>
            </div>
          </div>
        </Header>
      ) : (
        // Command Editor Screen
        <Header 
          headerClass="App-header2"
        >
          <div style={{ width: '100%', maxWidth: '1200px' }}>
            {/* Configuration indicator for command screen */}
            <div style={{
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              padding: '8px 16px',
              borderRadius: '20px',
              marginBottom: '20px',
              fontSize: '0.8rem',
              color: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <strong>Command Editor:</strong> {config.sceneTracking === 'scripts' ? 'Script events will be used instead of scoreboards' : 'Traditional scoreboard system'}
            </div>
            
            <CommandEditor 
              npcNames={npcData.npcNames}
              dialogues={npcData.dialogues}
              buttons={npcData.buttons}
              commands={npcData.commands}
              setCommands={npcData.setCommands}
              getPointedDialogues={npcData.getPointedDialogues}
              collapsedNpcs={npcData.collapsedNpcs}
              setCollapsedNpcs={npcData.setCollapsedNpcs}
              findNpcNameByTag={npcData.findNpcNameByTag}
              config={config}
              entityIds={entityIds}
            />
            <button 
              className="button-19 back-button"
              onClick={() => setScreen(1)}
            >
              Back to Main
            </button>
          </div>
        </Header>
      )}
    </div>
  );
}

export default App;